import { motion } from "framer-motion"
import { Trophy, Award, TrendingUp, Swords } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { useEffect, useState } from "react"

interface PlayerTotals {
  tournaments: number
  points: number
  winRate: number
  wins: number
  losses: number
  draws: number
}

const CountUp = ({ value }: { value: number }) => {
  const [display, setDisplay] = useState(0)

  useEffect(() => {
    let start = 0
    const duration = 800
    const increment = value / (duration / (2000 / 60))

    const counter = setInterval(() => {
      start += increment
      if (start >= value) {
        start = value
        clearInterval(counter)
      }
      setDisplay(Math.floor(start))
    }, 2000 / 60) // 60 FPS

    return () => clearInterval(counter)
  }, [value])

  return <>{display.toLocaleString()}</>
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.18,
      duration: 0.3,
    },
  }),
}

export const SummaryCards = ({ totals }: { totals: PlayerTotals }) => {
  const winBadge =
    totals.winRate >= 65
      ? { label: "Dominante", color: "text-green-500" }
      : totals.winRate >= 50
        ? { label: "Competitivo", color: "text-yellow-500" }
        : { label: "En desarrollo", color: "text-red-500" }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      {/* Torneos */}
      <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
        <Card className="group border border-border bg-card hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-primary/10 group-hover:scale-110 transition-transform">
              <Trophy className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Torneos</p>
              <p className="text-3xl font-bold text-foreground">
                <CountUp value={totals.tournaments} />
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Puntos */}
      <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
        <Card className="group border border-border bg-gradient-to-br from-yellow-500/5 to-transparent hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-yellow-500/10 group-hover:scale-110 transition-transform">
              <Award className="h-6 w-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Puntos</p>
              <p className="text-3xl font-bold text-foreground">
                <CountUp value={totals.points} />
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Winrate */}
      <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
        <Card className="group border border-border bg-gradient-to-br from-blue-500/5 to-transparent hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-3">
              <div className="p-3 rounded-2xl bg-blue-500/10 group-hover:scale-110 transition-transform">
                <TrendingUp className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Win Rate</p>
                <p className="text-3xl font-bold text-foreground">
                  <CountUp value={totals.winRate} />%
                </p>
              </div>
            </div>

            <p className={`text-xs font-semibold ${winBadge.color}`}>{winBadge.label}</p>
          </CardContent>
        </Card>
      </motion.div>

      {/* W/L/D */}
      <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
        <Card className="group border border-border bg-card hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 rounded-2xl bg-primary/10 group-hover:scale-110 transition-transform">
                <Swords className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Enfrentamientos</p>
                <p className="text-lg font-semibold text-foreground">Rendimiento</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-green-500 uppercase">Wins</p>
                <p className="text-2xl font-bold text-green-500">
                  <CountUp value={totals.wins} />
                </p>
              </div>
              <div>
                <p className="text-xs text-red-500 uppercase">Losses</p>
                <p className="text-2xl font-bold text-red-500">
                  <CountUp value={totals.losses} />
                </p>
              </div>
              <div>
                <p className="text-xs text-yellow-500 uppercase">Draws</p>
                <p className="text-2xl font-bold text-yellow-500">
                  <CountUp value={totals.draws} />
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
